import { DOCUMENT_TABLE_CELL_SPAN_MAXIMUM } from '@gitbook/api-client';
import type { Block } from '@gitbook/slate';
import { type Change, generateKey } from '@gitbook/slate';

import {
    type BlockTableData,
    FRAGMENT_TABLE_CELL,
    type TableCellMerge,
    type TableRecordValue,
} from '../types';
import { createTextFragment, isFragmentEmpty } from '../utils';
import { setRecordValue } from './changes';
import {
    getCellMergePositions,
    isMergeableColumnDefinition,
    type MergeableColumnDefinition,
    type ResolvedTableCellMerge,
    resolveCellMerges,
    type TableCellPosition,
    tableCellKey,
} from './mergeConstraints';
import { defaultRecordSortedKeys } from './utils';

export {
    canInsertAtCellMergeBoundary,
    getCellMergePositions,
    isColumnPartOfCellMerge,
    isMergeableColumnDefinition,
    isRecordPartOfCellMerge,
    preservesCellMergeOrder,
    type MergeableColumnDefinition,
    type ResolvedTableCellMerge,
    resolveCellMerges,
    type TableCellPosition,
    tableCellKey,
} from './mergeConstraints';

export interface TableCellRange {
    anchor: TableCellPosition;
    focus: TableCellPosition;
}

export interface ResolvedTableCellRangeBounds {
    records: string[];
    columns: string[];
}

export interface ResolvedTableCellRange extends ResolvedTableCellRangeBounds {
    cells: TableCellPosition[];
}

export interface TableCellRangeGrid {
    recordIds: readonly string[];
    columnIds: readonly string[];
    recordIndexById: ReadonlyMap<string, number>;
    columnIndexById: ReadonlyMap<string, number>;
    merges: readonly ResolvedTableCellMerge[];
}

/** Return the merge containing a table cell. */
export function getCellMerge(
    data: BlockTableData,
    recordId: string,
    columnId: string
): ResolvedTableCellMerge | undefined {
    return resolveCellMerges(data, defaultRecordSortedKeys(data), data.view.columns).find(
        (merge) => merge.records.includes(recordId) && merge.columns.includes(columnId)
    );
}

/** Return whether a cell is the visible anchor of a merge. */
export function isCellMergeAnchor(
    merge: ResolvedTableCellMerge,
    recordId: string,
    columnId: string
): boolean {
    return merge.merge.anchor.record === recordId && merge.merge.anchor.column === columnId;
}

/** Resolve and expand a rectangular selection so existing merged cells remain atomic. */
export function resolveCellRange(
    data: BlockTableData,
    recordOrder: string[],
    columnOrder: string[],
    range: TableCellRange
): ResolvedTableCellRange | null {
    const bounds = resolveCellRangeFromGrid(
        {
            recordIds: recordOrder,
            columnIds: columnOrder,
            recordIndexById: new Map(recordOrder.map((recordId, index) => [recordId, index])),
            columnIndexById: new Map(columnOrder.map((columnId, index) => [columnId, index])),
            merges: resolveCellMerges(data, recordOrder, columnOrder),
        },
        range
    );
    if (!bounds) return null;

    return {
        ...bounds,
        cells: bounds.records.flatMap((recordId) =>
            bounds.columns.map((definitionId) => ({ recordId, definitionId }))
        ),
    };
}

/** Resolve and expand a rectangular selection through a pre-indexed table grid. */
export function resolveCellRangeFromGrid(
    grid: TableCellRangeGrid,
    range: TableCellRange
): ResolvedTableCellRangeBounds | null {
    let firstRecord = grid.recordIndexById.get(range.anchor.recordId);
    let lastRecord = grid.recordIndexById.get(range.focus.recordId);
    let firstColumn = grid.columnIndexById.get(range.anchor.definitionId);
    let lastColumn = grid.columnIndexById.get(range.focus.definitionId);
    if (
        firstRecord === undefined ||
        lastRecord === undefined ||
        firstColumn === undefined ||
        lastColumn === undefined
    ) {
        return null;
    }

    [firstRecord, lastRecord] = [
        Math.min(firstRecord, lastRecord),
        Math.max(firstRecord, lastRecord),
    ];
    [firstColumn, lastColumn] = [
        Math.min(firstColumn, lastColumn),
        Math.max(firstColumn, lastColumn),
    ];

    let changed = true;
    while (changed) {
        changed = false;
        for (const merge of grid.merges) {
            const mergeFirstRecord = grid.recordIndexById.get(merge.records[0] ?? '');
            const mergeLastRecord = grid.recordIndexById.get(merge.records.at(-1) ?? '');
            const mergeFirstColumn = grid.columnIndexById.get(merge.columns[0] ?? '');
            const mergeLastColumn = grid.columnIndexById.get(merge.columns.at(-1) ?? '');
            if (
                mergeFirstRecord === undefined ||
                mergeLastRecord === undefined ||
                mergeFirstColumn === undefined ||
                mergeLastColumn === undefined ||
                mergeFirstRecord > lastRecord ||
                mergeLastRecord < firstRecord ||
                mergeFirstColumn > lastColumn ||
                mergeLastColumn < firstColumn
            ) {
                continue;
            }

            const nextFirstRecord = Math.min(firstRecord, mergeFirstRecord);
            const nextLastRecord = Math.max(lastRecord, mergeLastRecord);
            const nextFirstColumn = Math.min(firstColumn, mergeFirstColumn);
            const nextLastColumn = Math.max(lastColumn, mergeLastColumn);
            changed =
                nextFirstRecord !== firstRecord ||
                nextLastRecord !== lastRecord ||
                nextFirstColumn !== firstColumn ||
                nextLastColumn !== lastColumn;
            firstRecord = nextFirstRecord;
            lastRecord = nextLastRecord;
            firstColumn = nextFirstColumn;
            lastColumn = nextLastColumn;
        }
    }

    return {
        records: grid.recordIds.slice(firstRecord, lastRecord + 1),
        columns: grid.columnIds.slice(firstColumn, lastColumn + 1),
    };
}

/** Explain whether a resolved selection can be merged as one horizontal or vertical line. */
export function getMergeEligibility(
    data: BlockTableData,
    range: Pick<ResolvedTableCellRange, 'records' | 'columns'>
): { eligible: true } | { eligible: false; reason: string } {
    const isHorizontal = range.records.length === 1 && range.columns.length >= 2;
    const isVertical = range.columns.length === 1 && range.records.length >= 2;
    if (!isHorizontal && !isVertical) {
        return { eligible: false, reason: 'Select one row or one column to merge cells' };
    }
    const span = isHorizontal ? range.columns.length : range.records.length;
    if (span > DOCUMENT_TABLE_CELL_SPAN_MAXIMUM) {
        return {
            eligible: false,
            reason: `Select at most ${DOCUMENT_TABLE_CELL_SPAN_MAXIMUM} cells to merge`,
        };
    }
    const definitions = range.columns.map((columnId) => data.definition[columnId]);
    if (definitions.some((definition) => !definition)) {
        return { eligible: false, reason: 'The selected cells contain an unknown field' };
    }
    if (definitions.some((definition) => !isMergeableColumnDefinition(definition))) {
        return {
            eligible: false,
            reason: 'Cell merging currently supports Text and Number fields only',
        };
    }
    if (definitions.some((definition) => definition.type !== definitions[0].type)) {
        return { eligible: false, reason: 'Select cells with the same field type' };
    }
    return { eligible: true };
}

/** Return whether a mergeable cell has no meaningful content. */
export function isMergeValueEmpty(
    value: TableRecordValue | undefined,
    definition: MergeableColumnDefinition,
    tableBlock: Block
): boolean {
    if (definition.type === 'number') return value === null || typeof value === 'undefined';
    if (typeof value !== 'string') return true;
    const fragment = tableBlock.getFragmentByKey(value);
    return !fragment || isFragmentEmpty(fragment);
}

/** Merge a horizontal or vertical line of compatible cells. */
export function mergeCells(
    change: Change,
    tableKey: string,
    range: Pick<ResolvedTableCellRange, 'records' | 'columns'>,
    retainedCell: TableCellPosition
): boolean {
    const block = change.value.document.assertBlock(tableKey);
    const data = block.data.toFrozenObject() as BlockTableData;
    if (!isCurrentMergeRange(data, range)) return false;
    if (
        !range.records.includes(retainedCell.recordId) ||
        !range.columns.includes(retainedCell.definitionId)
    )
        return false;

    const anchor = { recordId: range.records[0], definitionId: range.columns[0] };
    const anchorDefinition = data.definition[anchor.definitionId];
    if (!isMergeableColumnDefinition(anchorDefinition)) return false;
    const retainedValue =
        data.records[retainedCell.recordId]?.values[retainedCell.definitionId] ??
        (anchorDefinition.type === 'number' ? null : undefined);
    if (anchorDefinition.type === 'text') {
        if (typeof retainedValue !== 'string' || !block.getFragmentByKey(retainedValue)) {
            return false;
        }
        setRecordValue(change, tableKey, anchor.recordId, anchor.definitionId, retainedValue, {
            normalize: false,
        });

        for (const cell of getRangeCells(range)) {
            if (cell.recordId === anchor.recordId && cell.definitionId === anchor.definitionId)
                continue;
            const fragmentKey = generateKey();
            change.insertFragmentByKey(
                tableKey,
                createTextFragment({ type: FRAGMENT_TABLE_CELL, fragment: fragmentKey }),
                undefined,
                { normalize: false }
            );
            setRecordValue(change, tableKey, cell.recordId, cell.definitionId, fragmentKey, {
                normalize: false,
            });
        }
    } else if (typeof retainedValue === 'number' || retainedValue === null) {
        setRecordValue(change, tableKey, anchor.recordId, anchor.definitionId, retainedValue, {
            normalize: false,
        });
        for (const cell of getRangeCells(range)) {
            if (cell.recordId === anchor.recordId && cell.definitionId === anchor.definitionId)
                continue;
            change.removeDataPathByKey(
                tableKey,
                ['records', cell.recordId, 'values', cell.definitionId],
                { normalize: false }
            );
        }
    } else {
        return false;
    }

    const selected = new Set(
        range.records.flatMap((recordId) =>
            range.columns.map((columnId) => tableCellKey(recordId, columnId))
        )
    );
    const otherMerges = resolveCellMerges(
        data,
        defaultRecordSortedKeys(data),
        data.view.columns
    ).filter((merge) =>
        getCellMergePositions(merge).every(
            (cell) => !selected.has(tableCellKey(cell.recordId, cell.definitionId))
        )
    );
    change.setDataByKey(
        tableKey,
        ['cellMerges'],
        [
            ...otherMerges.map((merge) => merge.merge),
            {
                anchor: { record: range.records[0], column: range.columns[0] },
                rowSpan: range.records.length,
                colSpan: range.columns.length,
            },
        ]
    );
    return true;
}

/** Remove the merge containing a cell. */
export function unmergeCell(
    change: Change,
    tableKey: string,
    recordId: string,
    columnId: string
): void {
    const data = change.value.document
        .assertBlock(tableKey)
        .data.toFrozenObject() as BlockTableData;
    const merge = getCellMerge(data, recordId, columnId);
    if (!merge) return;
    const cellMerges = (data.cellMerges ?? []).filter((candidate) => candidate !== merge.merge);
    if (cellMerges.length === 0) change.removeDataByKey(tableKey, ['cellMerges']);
    else change.setDataByKey(tableKey, ['cellMerges'], cellMerges);
}

/** Clear selected logical cells while retaining merge metadata and comments. */
export function clearCells(change: Change, tableKey: string, cells: TableCellPosition[]): void {
    for (const cell of cells) {
        change.removeDataPathByKey(
            tableKey,
            ['records', cell.recordId, 'values', cell.definitionId],
            { normalize: false }
        );
    }

    change.normalizeNodeByKey(tableKey);
}

/** Return whether vertical row spans require the native table renderer. */
export function hasVerticalCellMerges(data: BlockTableData): boolean {
    return resolveCellMerges(data, defaultRecordSortedKeys(data), data.view.columns).some(
        (merge) => merge.records.length > 1
    );
}

/** Normalize merge metadata against records, definitions, and visible order. */
export function normalizeCellMerges(data: BlockTableData): TableCellMerge[] {
    // Sorting the records is the expensive part, so skip it for the common merge-free table.
    if (!data.cellMerges?.length) return [];
    return resolveCellMerges(data, defaultRecordSortedKeys(data), data.view.columns).map(
        (merge) => merge.merge
    );
}

function isCurrentMergeRange(
    data: BlockTableData,
    range: Pick<ResolvedTableCellRange, 'records' | 'columns'>
): boolean {
    if (data.view.type !== 'grid' || !getMergeEligibility(data, range).eligible) return false;

    const recordOrder = defaultRecordSortedKeys(data);
    if (
        !isOrderedContiguous(recordOrder, range.records) ||
        !isOrderedContiguous(data.view.columns, range.columns)
    ) {
        return false;
    }

    const selected = new Set(
        range.records.flatMap((recordId) =>
            range.columns.map((definitionId) => tableCellKey(recordId, definitionId))
        )
    );
    return resolveCellMerges(data, recordOrder, data.view.columns).every((merge) => {
        const cells = getCellMergePositions(merge);
        const overlaps = cells.some((cell) =>
            selected.has(tableCellKey(cell.recordId, cell.definitionId))
        );
        return (
            !overlaps ||
            cells.every((cell) => selected.has(tableCellKey(cell.recordId, cell.definitionId)))
        );
    });
}

function isOrderedContiguous(order: readonly string[], ids: readonly string[]): boolean {
    const start = order.indexOf(ids[0]);
    return start >= 0 && ids.every((id, index) => order[start + index] === id);
}

function getRangeCells(
    range: Pick<ResolvedTableCellRange, 'records' | 'columns'>
): TableCellPosition[] {
    return range.records.flatMap((recordId) =>
        range.columns.map((definitionId) => ({ recordId, definitionId }))
    );
}
